import React from 'react';

/**
 * Presentational comparison matrix shared by the EOS + Final-Settlement what-if panels.
 * Columns are scenarios, rows are metrics. The first column (row labels) is sticky so the
 * metric names stay in view while the scenarios scroll horizontally. Purely presentational:
 * callers pass already-formatted cell nodes (including any delta chips / colour), so all the
 * numeric styling and i18n lives in the domain component.
 */
export interface ScenarioColumn {
  key: string;
  label: string;
  sublabel?: string;
  /** The scenario that matches the HR's current form inputs — highlighted + badged. */
  isCurrent?: boolean;
}

export interface ScenarioRow {
  key: string;
  label: string;
  /** One node per column, in column order. */
  cells: React.ReactNode[];
  /** Headline row (final amount) — rendered larger + heavier. */
  emphasis?: boolean;
}

interface Props {
  columns: ScenarioColumn[];
  rows: ScenarioRow[];
  /** Localised "Current" badge text. */
  currentLabel: string;
  /** Top-left header cell label (e.g. the metric-column heading). */
  metricHeader: string;
}

export const ScenarioMatrix: React.FC<Props> = ({ columns, rows, currentLabel, metricHeader }) => (
  <div className="overflow-x-auto rounded-xl border border-border" style={{ fontVariantNumeric: 'tabular-nums' }}>
    <table className="w-full min-w-[34rem] border-collapse text-sm">
      <thead>
        <tr className="border-b border-border">
          <th className="sticky start-0 z-10 min-w-[9rem] bg-muted/60 px-4 py-3 text-start text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {metricHeader}
          </th>
          {columns.map(col => (
            <th
              key={col.key}
              className={`min-w-[8.5rem] px-4 py-3 text-end align-bottom ${col.isCurrent ? 'bg-primary/5' : 'bg-muted/40'}`}
            >
              <div className="flex flex-col items-end gap-1">
                <span className="text-sm font-semibold text-foreground">{col.label}</span>
                {col.sublabel && (
                  <span className="text-xs font-normal text-muted-foreground">{col.sublabel}</span>
                )}
                {col.isCurrent && (
                  <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                    {currentLabel}
                  </span>
                )}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {rows.map(row => (
          <tr key={row.key}>
            <th
              scope="row"
              className={`sticky start-0 z-10 bg-card px-4 py-3 text-start font-medium text-muted-foreground ${row.emphasis ? 'text-sm' : 'text-sm'}`}
            >
              {row.label}
            </th>
            {row.cells.map((cell, i) => (
              <td
                key={columns[i]?.key ?? i}
                className={`px-4 py-3 text-end ${columns[i]?.isCurrent ? 'bg-primary/5' : ''} ${
                  row.emphasis ? 'text-base font-semibold text-foreground' : 'text-foreground'
                }`}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default ScenarioMatrix;
